import type { FitRule } from "@slot/presentation";
import { BASE_ROWS, GRID_SIZE } from "../gridSize";

/**
 * The composition's geometry in design units, y-down, its origin at the BASE grid's centre (row 1's centre): the
 * shell's grid loading overlay sits there. The art is the artist's delivery (`Supporting Files/coin-hold-assets.*`),
 * drawn at `ART_SCALE` design units per delivery pixel — the scale the user's mock-up (`reference.png`) shows it at —
 * and placed as the mock-up places it: two jackpot plaques stacked on each side at the top, the three pots behind a heap
 * of coins on the frame's top edge, the gold-framed grid (its middle row framed again), the respin meter hanging over
 * the frame's bottom edge. Every art measure here is in the DELIVERY's pixels; the imported images are smaller, and the
 * views size every sprite in design units.
 */

/** Design units per pixel of the delivery. */
export const ART_SCALE = 0.35;

/** A delivery measure in design units. */
export const art = (px: number): number => px * ART_SCALE;

/** Row 1's centre in the mock-up's pixels (`new coin.psd`, 3042 wide): the composition's origin there. */
const MOCKUP_ORIGIN = { x: 1526.5, y: 2901 } as const;

/** A point of the mock-up (the artist's Rive handoff places everything in its pixels) in design units. */
export const fromMockup = (x: number, y: number): { x: number; y: number } => ({ x: art(x - MOCKUP_ORIGIN.x), y: art(y - MOCKUP_ORIGIN.y) });

/**
 * The grid frame's art (`gridFrame`, 2942 × 1619): three rows of four windows between gold bars, a glow around it and a
 * shadow under it. A frame of any height is built from three slices cut through the middles of the bars under rows 0
 * and 1: the top border with row 0, row 1 (repeated once more for every row past the base rows), row 2 with the bottom.
 */
export const FRAME_ART = {
  width: 2942,
  height: 1619,
  /** Row 1's window centre: the composition's origin. */
  origin: { x: 1471, y: 788.5 },
  /** Between two windows' centres. */
  pitch: { x: 672, y: 454 },
  /** A window: where a cell shows between the bars. */
  window: { width: 640, height: 424 },
  /** Where the slices meet. */
  slices: { top: 561, bottom: 1015 },
  /** The gold, without the glow around it. */
  body: { left: 25, top: 32, right: 2917, bottom: 1593 },
} as const;

/** The middle row's own frame (`centerRowGridFrame`, 2942 × 713): its window centre sits on row 1's. */
export const ROW_FRAME_ART = { width: 2942, height: 713, windowCentreY: 335 } as const;

export const CELL = {
  /** The distance between two cells' centres. */
  pitchX: art(FRAME_ART.pitch.x),
  pitchY: art(FRAME_ART.pitch.y),
  /** A cell's window between the frame's bars: a rolling cell's strip shows through it. */
  window: { width: art(FRAME_ART.window.width), height: art(FRAME_ART.window.height) },
  /** A cell's background (`blueBg`, the tease's and the trigger's `redBg`, 666 × 442), its edges under the bars. */
  background: { width: art(666), height: art(442) },
  /** The blank (`blueGlow`, 551 × 289): the empty cell's plaque, which rolls with the coins. */
  blank: { width: art(551), height: art(289) },
} as const;

/**
 * The coins' drawn size: the delivered coin's image, its glow included (the disc is 0.9 of it, 233 across): nearly a
 * cell's pitch wide, so a coin overflows its cell's height, as in the mock-up.
 */
export const COIN_SIZE = 259;

/** Each row's centre y; row 1 (the middle base row) is the origin, the GROW rows continue downwards. */
export function rowCentreY(row: number): number {
  return (row - 1) * CELL.pitchY;
}

/** Each column's centre x. */
export function columnCentreX(col: number): number {
  return (col - (GRID_SIZE.columns - 1) / 2) * CELL.pitchX;
}

/** The grid's layout area (every row, the GROW rows included): the grid layer never resizes a cell. */
export const GRID_AREA = { width: GRID_SIZE.columns * CELL.pitchX, height: GRID_SIZE.rows * CELL.pitchY } as const;

/** Where the layout area's centre sits: with row 1 at the origin, the 5-row area's centre (row 2's) is one row lower. */
export const GRID_AREA_OFFSET_Y = rowCentreY((GRID_SIZE.rows - 1) / 2);

/** The frame's gold for `rows` rows in play (fractional while it grows): the base frame and a pitch per GROW row. */
export function frameBox(rows: number): { x: number; y: number; width: number; height: number } {
  const { body, origin } = FRAME_ART;
  const top = art(body.top - origin.y);
  const bottom = art(body.bottom - origin.y) + (rows - BASE_ROWS) * CELL.pitchY;
  return { x: art(body.left - origin.x), y: top, width: art(body.right - body.left), height: bottom - top };
}

/**
 * The clip the symbols show through: the frame's gold for the rows in play and room around it for the coins (a landed
 * coin overflows its cell, a popping one more). Below, the room is there only while the rows are settled: a hidden GROW
 * row draws nothing, but while the frame grows or shrinks its edge is where the GROW rows show.
 */
export function panelClip(rows: number): { x: number; y: number; width: number; height: number } {
  const frame = frameBox(rows);
  const room = 48;
  const below = Number.isInteger(rows) ? room : 0;
  return { x: frame.x - room, y: frame.y - room, width: frame.width + 2 * room, height: frame.height + room + below };
}

/**
 * A rolling cell's window: the frame's window, so the strip (the blanks and the coins) rolls inside its cell and a coin
 * overflows its cell only once it has landed (its pop carries it out of the window). The lane spacing keeps the
 * neighbours out of the window at rest: half the window plus half a coin, the tallest symbol.
 */
export const ROLL_WINDOW = {
  width: CELL.window.width,
  height: CELL.window.height,
  laneSpacing: Math.ceil(CELL.window.height / 2 + COIN_SIZE / 2) + 4,
} as const;

/**
 * The pots on the frame's top edge, their feet at it (LIFE left, MULTI larger in the middle, GROW right): behind a heap
 * of coins (`potBgCoins`, drawn larger than the rest of the art, its lower half behind the grid, as in the mock-up),
 * each pot's name (`lifeText`…) over its base. The urns and their names where the mock-up draws them, matched against
 * it from row 1's centre: the urns at the art's scale, each name at the mock-up's (a hair under it) and off its urn's
 * centre by `x`, `y` (design units). Art sizes in the delivery's pixels.
 */
export const POTS_LAYOUT = {
  life: { x: -321.8, y: -374.5, art: { width: 793, height: 625 }, label: { width: 408, height: 182, scale: 0.99, x: 0, y: 78 } },
  multi: { x: -3.3, y: -384.7, art: { width: 930, height: 732 }, label: { width: 540, height: 183, scale: 0.996, x: 11.2, y: 92.4 } },
  grow: { x: 311.3, y: -374.5, art: { width: 782, height: 625 }, label: { width: 527, height: 184, scale: 0.99, x: 6.7, y: 82.3 } },
  /** Every pot's mouth (the same urn drawn three times): its front lip from the pot's centre and its width, as fractions of the pot's art. */
  mouth: { y: -0.332, width: 0.62 },
  /**
   * Each pot's opening, where its gems fly to and its impact plays: the centre of the potion's surface, as the artist's
   * handoff places it (`manifest.json`, in the mock-up's pixels).
   */
  opening: { life: fromMockup(610.1, 1585.0), multi: fromMockup(1525.2, 1513.0), grow: fromMockup(2418.1, 1584.4) },
  heap: { x: 0, y: -254, art: { width: 1928, height: 504 }, scale: 0.44 },
} as const;

/**
 * The pots' artboard (the artist's `potion_pots.riv`, 2850 × 1150 at the delivery's scale, as Spine) is the
 * mock-up's pots area, cropped at its (100, 1150): it goes where the mock-up has it, at the art's scale, and its urns
 * land on the delivery's. Each pot is its own part of it (`parts`, from–to in artboard pixels, the full height): the
 * columns between the urns are empty at every level (measured: 904–952 and 1882–1924), so a part holds its pot whole,
 * bubbles and overflow, and moves on its own (a gem's bump, an eruption).
 */
export const POTION_POTS_LAYOUT = {
  artboard: { width: 2850, height: 1150 },
  topLeft: fromMockup(100, 1150),
  parts: { life: [0, 928], multi: [928, 1903], grow: [1903, 2850] },
} as const;

/**
 * The artist's grid edge fire (`edge_fire.riv`: two artboards of 3564 × 1218 at the mock-up's scale): GROW's blue fire on
 * the frame's top and bottom edges as the grid grows. Each piece's anchor is a corner of the frame the artist drew it
 * around (`frameWidth` wide): the top piece's its outer top-left corner (`top`), the bottom piece's its outer bottom-left
 * (`bottom`). The game's frame is wider (`frameBox`): each piece is drawn to its width, from its own corner. Each piece
 * runs down (or up) the sides to tips that meet in the middle of the base frame's sides; `side` is its two side columns
 * apart from its edge and corners (`width` from the artboard's left and right edges; the top piece's from `top` down,
 * the bottom piece's down to `bottom`), drawn longer down a grown frame's sides so that the tips meet again.
 */
export const EDGE_FIRE_LAYOUT = {
  artboard: { width: 3564, height: 1218 },
  top: { x: 380, y: 380 },
  bottom: { x: 380, y: 838 },
  frameWidth: 2804,
  side: { width: 580, top: 640, bottom: 578 },
} as const;

/**
 * A cell's multiplier in the bonus (MULTI's): its "×n" over the coin's rim at the cell's top-right corner, `offset` from the
 * cell's centre (grid-root units), its digits `fontSize` tall. As it rides a MULTI fire streak's head, and is caught on
 * its cell's centre as the head gets there, its digits are `caughtFontSize` tall, about the cell's size (the user,
 * 2026-10-12); it pops there, then scales down to `fontSize` on its way to the corner.
 */
export const MULTIPLIER_LAYOUT = { offset: { x: 72, y: -58 }, fontSize: 60, caughtFontSize: 150 } as const;

/**
 * The artist's cell fire (`cell_fire.riv`: two artboards at the mock-up's scale, each centred on its cell's centre):
 * `hit`, the burst as MULTI's fireball reaches a cell (its ring about the cell's height, its flames a little past it),
 * and `fire`, a cell with a multiplier burning, which the artist fitted inside its window: drawn `fireScale` times
 * that, a little bigger than the cell (the user, 2026-10-12). The middle row's window is taller than the others' (the
 * artist's 423 against 388): its fire is stretched to it.
 */
export const CELL_FIRE_LAYOUT = {
  hit: { width: 1200, height: 1000 },
  fire: { width: 680, height: 428 },
  fireScale: 1.15,
  middleRowStretch: 423 / 388,
} as const;

/**
 * The designer's mystery jackpot (`mystery reel/`, image elements, 2026-10-12), in its frame's pixels, the frame's
 * top-left the origin: the window the bands show through (its centre the symbol's), a jackpot's band and the taller
 * MYSTERY band, the frame-sized pieces' sides, and the assembled board symbol, its "MYSTERY JACKPOT" overhanging the frame
 * by `symbol.frameX` on either side. On the board the frame is a coin's disc: 690 of the mock-up's pixels, `coinScale` of
 * its own, at the mock-up's scale (`MYSTERY_BOARD_SCALE`).
 */
export const MYSTERY_REEL_LAYOUT = {
  coinScale: 0.4887,
  frame: { width: 1412, height: 1358 },
  window: { x: 704.5, y: 666, radius: 494.8 },
  band: { width: 1014, height: 356, mysteryHeight: 463 },
  text: { width: 1670, height: 892 },
  symbol: { width: 1670, height: 1358, frameX: 131 },
  glow: 2118,
  mask: 997,
  sparkle: 80,
} as const;

/** Design units per frame pixel of the mystery jackpot on the board. */
export const MYSTERY_BOARD_SCALE = MYSTERY_REEL_LAYOUT.coinScale * ART_SCALE;

/**
 * The artist's idle coin (`coin_idle.riv`: a 1000 × 1000 artboard at the mock-up's scale, its centre the coin's): the coin
 * 673 × 660 of its pixels, the mock-up's coin (its disc 235 design units, as the still coin's 348 of 384 at `COIN_SIZE`),
 * the rest room for its glow.
 */
export const COIN_IDLE_LAYOUT = { artboard: { width: 1000, height: 1000 }, coin: { width: 673, height: 660 } } as const;

/**
 * The artist's collector (`collector.riv`: a 1400 × 1400 artboard at the mock-up's scale, its centre the cell's): the
 * symbol about 690 of its pixels wide, as the coin's disc (235 design units of its 259), the rest room for its flare.
 */
export const COLLECTOR_LAYOUT = { artboard: { width: 1400, height: 1400 } } as const;

/**
 * The artist's bonus transition (`bonus_transition.riv`: a 2048 × 2048 artboard, its explosion at its centre): drawn over
 * the whole screen, its square centred on the screen's centre and covering it.
 */
export const BONUS_TRANSITION_LAYOUT = { artboard: { width: 2048, height: 2048 } } as const;

/**
 * The artist's board impact (`board_impact.riv`: an artboard of 3300 × 2400 at the mock-up's scale): GROW's blue
 * explosion as its fire streak reaches the board's centre, its hit point the artboard's centre (`hit`), its side waves
 * racing to the sides of the frame the artist drew it around (`frameWidth` wide). The game's frame is wider: it is drawn
 * to it, as the edge fire is.
 */
export const BOARD_IMPACT_LAYOUT = {
  artboard: { width: 3300, height: 2400 },
  hit: { x: 1650, y: 1200 },
  frameWidth: 2804,
} as const;

/**
 * Design units per artboard pixel of the artist's board effects (the edge fire, the board impact), each drawn around a
 * frame `frameWidth` of the mock-up's pixels wide: that frame drawn to the game's, which is wider (`frameBox`).
 */
export function handoffFrameUnits(frameWidth: number): number {
  return (frameBox(BASE_ROWS).width / art(frameWidth)) * ART_SCALE;
}

/**
 * The artist's pot shine (`pot_shine.riv`, an artboard of 2850 × 1550 at the mock-up's scale): a warm red-gold shine
 * behind the three pots, its rays rising from behind them to about the bottom of the MAJOR and MINI plaques. Its
 * artboard's top-left is the mock-up's (100, 600): the pots' artboard's x, 550 px higher.
 */
export const POT_SHINE_LAYOUT = { artboard: { width: 2850, height: 1550 }, topLeft: fromMockup(100, 600) } as const;

/**
 * The artist's impact (`impact.riv`, an artboard of 1600 × 1900 at the mock-up's scale): the burst as a light reaches its
 * pot — a flash, a glow over the whole pot, rays, a shockwave, a splash — its `hit` point on the pot's opening.
 */
export const IMPACT_LAYOUT = { artboard: { width: 1600, height: 1900 }, hit: { x: 800, y: 820 } } as const;

/**
 * The artist's pot smoke (`pot_smoke.riv`, an artboard of 1100 × 1450 at the mock-up's scale): a pot's plume while its
 * booster plays, rising from its `base` — on the pot's opening, where its impact plays.
 */
export const POT_SMOKE_LAYOUT = { artboard: { width: 1100, height: 1450 }, base: { x: 550, y: 1300 } } as const;

/**
 * How far the artist's pot eruption draws past its artboard's top and bottom, in its pixels: its jets, up and off the
 * screen, and the MULTI pot's glow, which Rive cut at the artboard (measured over every frame and colour of its
 * conversion, every pixel drawn at 1/255 or more: 1624 over, 302 under; the user, 2026-10-11: let them fly).
 */
const ERUPTION_REACH = { above: 1640, below: 320 } as const;

/**
 * The artist's pot eruption (`pot_eruption.riv`, an artboard of 1800 × 2000 at the mock-up's scale): a pot erupting — a
 * crater flash and a ring of fire on its opening, jets up and off the screen, a glow over the pot — its `base` on the
 * pot's opening, where its impact and its smoke play. It is drawn over its artboard grown by its reach
 * (`ERUPTION_REACH`), so nothing it draws is cut: `artboard` is that area, `base` its point in it.
 */
export const POT_ERUPTION_LAYOUT = {
  artboard: { width: 1800, height: 2000 + ERUPTION_REACH.above + ERUPTION_REACH.below },
  base: { x: 900, y: 1300 + ERUPTION_REACH.above },
} as const;

/**
 * The artist's fire streak (`fire_streak.riv`, an artboard of 1900 × 900 at the mock-up's scale): a fireball flying
 * right, its `head` the point that rides its fall and the centre of the ring of fire it lands in (about a cell across).
 */
export const FIRE_STREAK_LAYOUT = { artboard: { width: 1900, height: 900 }, head: { x: 1450, y: 450 } } as const;

/**
 * The artist's flying light (`flying_light.riv`, an artboard of 1000 × 320): a comet flying right, its head's middle at
 * `head` (the anchor: the head rides the flight's path, the tail trailing) and its bright core `headPixels` across.
 */
export const FLYING_LIGHT_LAYOUT = { artboard: { width: 1000, height: 320 }, head: { x: 800, y: 160 }, headPixels: 185 } as const;

/**
 * The jackpot plaques (`grand`, `major`, `minor`, `mini`; the delivery has no ROYAL, so a stand-in drawn after GRAND,
 * set as GRAND): GRAND over MAJOR on the left, MINOR over MINI on the right, each its name on a tab over a bar the
 * amount goes in. At the art's scale and where the mock-up draws them (each art matched against it, from row 1's
 * centre). Each amount as the mock-up sets it, in the delivery's pixels: its digits' height (the cap), their centre
 * below the plaque's, and the widest line it may take (the bar's face inside the rim, less the outline). `rim` is the
 * bar's gold rim in the art (left, top, right, bottom), where the plaque's sparkles twinkle.
 */
export const PLAQUES_LAYOUT = {
  scale: ART_SCALE,
  art: {
    grand: { width: 809, height: 460, amount: { cap: 80, y: 57, width: 667 }, rim: [43, 170, 765, 405] },
    royal: { width: 809, height: 460, amount: { cap: 80, y: 57, width: 667 }, rim: [43, 170, 765, 405] },
    major: { width: 716, height: 411, amount: { cap: 69, y: 46.5, width: 578 }, rim: [41, 148, 674, 357] },
    minor: { width: 711, height: 409, amount: { cap: 69, y: 45.5, width: 573 }, rim: [41, 149, 669, 355] },
    mini: { width: 609, height: 375, amount: { cap: 61, y: 50.5, width: 473 }, rim: [41, 144, 567, 321] },
  },
  grand: { x: -368.4, y: -760.7 },
  major: { x: -368, y: -619 },
  minor: { x: 363.5, y: -760 },
  mini: { x: 363.1, y: -609.2 },
} as const;

/**
 * The respin meter (`spinLifeFrame`, 1027 × 256, three windows) hanging over the frame's bottom edge, as in the mock-up.
 * A lit slot is `spinLifeFill` (287 × 170) with its number; an unlit one stretches the purple `spinLifeBg` to its size.
 */
export const METER_LAYOUT = {
  art: { width: 1027, height: 256 },
  /** The windows' centres from the frame art's centre. */
  slots: [-319, -1, 318],
  slotY: -10,
  slot: { width: 287, height: 170 },
  /** The meter's centre from the frame's bottom edge (negative: it overlaps the edge). */
  belowFrame: -23,
  fontSize: 46,
} as const;

/**
 * The respin meter's flames while LIFE is in play: the artist's spin counter (a Rive artboard of 1240 × 560 at the
 * delivery's scale), read out by the art import into `game-deferred/meter-flames`. The fire is a flipbook of `frames`
 * frames of `frame` pixels (half the artboard); it and its glow cover the artboard, centred on the meter's plate and
 * `artboardY` above it (the room over the plate is the sparks'). The plate is the meter
 * frame art's body (995 × 224, the artboard's own plate), its centre `plate` from the art's. Sparks rise out of the
 * fire's top edge: they start within `sparks.spawn`, in the artboard's pixels.
 */
export const METER_FLAMES_LAYOUT = {
  artboard: { width: 1240, height: 560 },
  artboardY: -42,
  plate: { x: -0.5, y: -9.5 },
  frames: 60,
  frame: { width: 620, height: 280 },
  sparks: { size: 80, spawn: { left: 150, right: 1090, top: 140, bottom: 175 } },
} as const;

/**
 * The LINES selector under the grid in the base game (decision 56; placeholder art after 3 Coin Volcanoes', drawn at two
 * pixels per design unit): a gold frame with its name on a tab, the count in a red window between two green arrows. It
 * hangs from the frame's bottom edge where the respin meter hangs in the bonus, the tab over the frame's bar. Positions
 * from its centre.
 */
export const LINES_LAYOUT = {
  panel: { width: 300, height: 110 },
  /** The name on the tab: its centre and the box its text fits. */
  name: { y: -41, width: 118, height: 30, fontSize: 30 },
  count: { y: 10, fontSize: 58 },
  arrow: { x: 102, y: 10, size: 70 },
  /** Its centre below the frame's bottom edge. */
  belowFrame: 36,
} as const;

/** The logo between the plaques' columns, clear of them (its art is 1100 × 760). */
export const LOGO_LAYOUT = { width: 248, height: 171, x: 0, y: -774 } as const;

/**
 * The title's intro (decision 77; `views/intro/CoinHoldIntro.ts`), after 3 Coin Volcanoes': the logo over the three
 * pots in a triangle — LIFE on top over the respin meter, MULTI and GROW below over a MAJOR coin and the ROYAL plaque —
 * the heading under them, in a box fitted to the screen (never above its size), its origin the screen's centre.
 */
export const INTRO_LAYOUT = {
  box: { width: 1000, height: 1560 },
  logo: { y: -630, width: 500 },
  pots: { life: { x: 0, y: -300 }, multi: { x: -255, y: 120 }, grow: { x: 255, y: 120 } },
  potWidth: 300,
  /** A pot's name plate over its base: its centre from the pot's, and its width, as shares of the pot's height and width. */
  potLabel: { y: 0.36, width: 0.62 },
  /** Each pot's glow behind it, as wide as this many pots. */
  glow: 1.7,
  meter: { x: 0, y: -80, scale: 0.92 },
  coin: { x: -255, y: 360, size: 210 },
  /** The ×2 MULTI drops on the coin: where it lands from the coin's centre, how far above it sets out, its size; its number after it goes in. */
  badge: { corner: { x: 70, y: -70 }, from: -320, scale: 1.25, merged: { y: 52, scale: 1 } },
  plaque: { x: 255, y: 360, scale: 1.15 },
  heading: { y: 590, fontSize: 66, box: { width: 920, height: 96 } },
} as const;

/**
 * The composition's extent (top: the logo and the plaques; bottom: what hangs under the frame — the LINES selector in
 * the base game, the meter in the bonus) with the base rows and with all five (GROW: the bonus, the meter).
 */
const TOP = Math.min(LOGO_LAYOUT.y - LOGO_LAYOUT.height / 2, PLAQUES_LAYOUT.grand.y - (PLAQUES_LAYOUT.art.grand.height * PLAQUES_LAYOUT.scale) / 2);
const meterBottom = (rows: number): number => {
  const frame = frameBox(rows);
  return frame.y + frame.height + METER_LAYOUT.belowFrame + art(METER_LAYOUT.art.height) / 2;
};
const linesBottom = (): number => {
  const frame = frameBox(BASE_ROWS);
  return frame.y + frame.height + LINES_LAYOUT.belowFrame + LINES_LAYOUT.panel.height / 2;
};
export const COMPOSITION = {
  width: frameBox(BASE_ROWS).width,
  top: TOP,
  bottom: Math.max(meterBottom(BASE_ROWS), linesBottom()),
  grownBottom: meterBottom(GRID_SIZE.rows),
} as const;

/**
 * How the composition fits each shape, with the base rows and with all five (GROW zooms out). The control bar overlays
 * the scene in portrait and on desktop, so each box keeps room under the content for it; portrait's box is the screen's
 * width, so the composition fills it; the wide shapes keep the content's column in the middle.
 */
function fitFor(bottom: number): FitRule {
  const free = { desktop: 230, mobile: 470, mobile_landscape: 40 } as const;
  const box = (shape: keyof typeof free, width: number) => {
    const top = COMPOSITION.top - 30;
    const height = bottom + free[shape] - top;
    return { width, height, centerY: top + height / 2 };
  };
  return {
    box: { desktop: box("desktop", 1500), mobile: box("mobile", 1080), mobile_landscape: box("mobile_landscape", 1400) },
    limits: { desktop: { maxWidth: 2400 }, mobile: { maxWidth: 1080 }, mobile_landscape: {} },
    offsetY: { desktop: 0, mobile: 0, mobile_landscape: 0 },
  };
}

export const COMPOSITION_FIT = { base: fitFor(COMPOSITION.bottom), grown: fitFor(COMPOSITION.grownBottom) } as const;

